import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Share2,
  Download,
  ArrowLeft,
  BookmarkPlus,
  Sparkles,
} from "lucide-react";
import { CALCULATORS } from "./calculators";
import { Checkbox } from "../ui/checkbox";
import { fmtINR } from "../../lib/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const COLORS = ["#10b981", "#1e3a8a", "#f59e0b", "#ef4444", "#a855f7"];

function NumberInput({ field, value, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {field.label}
        </label>
        <div className="flex items-center gap-1">
          <input
            type="number"
            data-testid={`calc-input-${field.key}`}
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="w-32 text-right text-sm font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 num-mono"
          />
          <span className="text-xs text-slate-500 min-w-[28px]">
            {field.unit}
          </span>
        </div>
      </div>
      <input
        type="range"
        data-testid={`calc-slider-${field.key}`}
        min={field.min}
        max={field.max}
        step={field.step}
        value={Math.min(Math.max(Number(value) || 0, field.min), field.max)}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="w-full h-1.5 accent-emerald-600"
      />
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>
          {field.unit === "₹"
            ? fmtINR(field.min, { compact: true })
            : field.min}
        </span>
        <span>
          {field.unit === "₹"
            ? fmtINR(field.max, { compact: true })
            : field.max}
        </span>
      </div>
    </div>
  );
}

function ResultStat({
  label,
  value,
  accent = "text-slate-900 dark:text-white",
  testid,
}) {
  return (
    <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div
        data-testid={testid}
        className={`mt-1 num-mono text-2xl font-bold ${accent}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function CalculatorShell({ slug }) {
  const cfg = CALCULATORS[slug];
  const { user } = useAuth();
  const [inputs, setInputs] = useState({ ...(cfg?.defaults || {}) });

  const onChange = (key, value) =>
    setInputs((prev) => ({ ...prev, [key]: value }));
  const onToggle = (key) =>
    setInputs((prev) => ({ ...prev, [key]: !prev[key] }));

  const result = useMemo(() => (cfg ? cfg.run(inputs) : null), [cfg, inputs]);

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([k, v]) => params.set(k, String(v)));
    return `${window.location.origin}/calculators/${slug}?${params.toString()}`;
  }, [inputs, slug]);

  if (!cfg) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="text-center">
          <p className="text-2xl font-display font-bold">
            Calculator not found
          </p>
          <Link
            to="/calculators"
            className="text-emerald-600 mt-2 inline-block"
          >
            ← Back to all calculators
          </Link>
        </div>
      </div>
    );
  }

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const onSave = async () => {
    if (!user?.id) {
      toast.info("Sign in to save calculations");
      return;
    }
    try {
      await api.post("/calculators/save", { slug, inputs, result });
      toast.success("Saved to your history");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const onExport = () => {
    const lines = [`${cfg.name} — AccessWealth`, ""];
    Object.entries(inputs).forEach(([k, v]) => lines.push(`${k}: ${v}`));
    lines.push("---");
    Object.entries(result).forEach(([k, v]) => {
      if (Array.isArray(v) || typeof v === "object") return;
      lines.push(`${k}: ${v}`);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-result.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen mesh-bg">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/calculators"
          data-testid="back-to-calculators"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-emerald-600 mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> All calculators
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <Sparkles className="h-3 w-3" /> {cfg.category}
            </div>
            <h1
              data-testid="calc-page-title"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white mt-3"
            >
              {cfg.name}
            </h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400 max-w-2xl">
              {cfg.about}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onShare}
              data-testid="calc-share-btn"
              className="btn-secondary !py-2 !px-3 !text-xs"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
            <button
              onClick={onExport}
              data-testid="calc-export-btn"
              className="btn-secondary !py-2 !px-3 !text-xs"
            >
              <Download className="h-3.5 w-3.5" /> Export
            </button>
            <button
              onClick={onSave}
              data-testid="calc-save-btn"
              className="btn-primary !py-2 !px-3 !text-xs"
            >
              <BookmarkPlus className="h-3.5 w-3.5" /> Save
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 card-soft p-6 space-y-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Inputs
            </div>

            {cfg.fields.map((f) => (
              <NumberInput
                key={f.key}
                field={f}
                value={inputs[f.key]}
                onChange={onChange}
              />
            ))}

            {cfg.select && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {cfg.select.label}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {cfg.select.options.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`calc-select-${opt}`}
                      onClick={() => onChange(cfg.select.key, opt)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold capitalize transition-all border ${
                        inputs[cfg.select.key] === opt
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-500"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {cfg.toggle && (
              <label className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {cfg.toggle.label}
                </span>

                <Checkbox
                  data-testid={`calc-toggle-${cfg.toggle.key}`}
                  checked={!!inputs[cfg.toggle.key]}
                  onCheckedChange={() => onToggle(cfg.toggle.key)}
                />
              </label>
            )}
          </div>

          <div className="lg:col-span-3 space-y-6">
            <ResultsBlock slug={slug} result={result} cfg={cfg} />
            <ChartBlock slug={slug} result={result} cfg={cfg} />
          </div>
        </div>

        {cfg.formula && (
          <div className="mt-8 card-soft p-6">
            <div className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              How this calculator works
            </div>

            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4 overflow-x-auto">
              <code className="text-emerald-600 font-mono text-sm break-words">
                {cfg.formula}
              </code>
            </div>

            {cfg.formulaExplanation && (
              <ul className="mt-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
                {cfg.formulaExplanation.map((line, idx) => (
                  <li key={idx}>• {line}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultsBlock({ slug, result, cfg }) {
  if (slug === "emi" || slug.endsWith("-loan")) {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Monthly EMI"
          value={fmtINR(result.emi)}
          accent="text-emerald-600"
          testid="result-emi"
        />
        <ResultStat
          label="Total Interest"
          value={fmtINR(result.total_interest)}
          testid="result-total-interest"
        />
        <ResultStat
          label="Total Payment"
          value={fmtINR(result.total_payment)}
          testid="result-total-payment"
        />
      </div>
    );
  }
  if (slug === "cagr") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="CAGR"
          value={`${result.cagr_percent}%`}
          accent="text-emerald-600"
          testid="result-cagr"
        />
        <ResultStat
          label="Absolute Return"
          value={fmtINR(result.absolute_return)}
          testid="result-abs-return"
        />
        <ResultStat
          label="Absolute %"
          value={`${result.absolute_return_percent}%`}
          testid="result-abs-pct"
        />
      </div>
    );
  }
  if (slug === "swp") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Final Balance"
          value={fmtINR(result.final_balance)}
          accent="text-emerald-600"
          testid="result-final-balance"
        />
        <ResultStat
          label="Total Withdrawn"
          value={fmtINR(result.total_withdrawn)}
          testid="result-total-withdrawn"
        />
        <ResultStat
          label="Starting Corpus"
          value={fmtINR(result.corpus)}
          testid="result-corpus"
        />
      </div>
    );
  }
  if (slug === "fire") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Years to FIRE"
          value={result.years_to_fire}
          accent="text-emerald-600"
          testid="result-fire-years"
        />
        <ResultStat
          label="FIRE Corpus"
          value={fmtINR(result.fire_corpus)}
          testid="result-fire-corpus"
        />
        <ResultStat
          label="Final Balance"
          value={fmtINR(result.final_balance)}
          testid="result-fire-final"
        />
      </div>
    );
  }
  if (slug === "retirement") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Corpus Needed"
          value={fmtINR(result.corpus_required)}
          accent="text-emerald-600"
          testid="result-corpus-required"
        />
        <ResultStat
          label="Monthly SIP"
          value={fmtINR(result.monthly_sip)}
          testid="result-monthly-sip"
        />
        <ResultStat
          label="Future Expense / mo"
          value={fmtINR(result.future_monthly_expense)}
          testid="result-future-expense"
        />
      </div>
    );
  }
  if (slug === "inflation") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Future Cost"
          value={fmtINR(result.future_cost)}
          accent="text-emerald-600"
          testid="result-future-cost"
        />
        <ResultStat
          label="Purchasing Power"
          value={fmtINR(result.purchasing_power)}
          testid="result-power"
        />
        <ResultStat
          label="Loss in Value"
          value={fmtINR(result.loss_in_value)}
          testid="result-loss"
        />
      </div>
    );
  }
  if (slug === "gst") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Base Amount"
          value={fmtINR(result.base_amount)}
          testid="result-base"
        />
        <ResultStat
          label="GST Amount"
          value={fmtINR(result.gst_amount)}
          accent="text-emerald-600"
          testid="result-gst"
        />
        <ResultStat
          label="Total"
          value={fmtINR(result.total_amount)}
          testid="result-total"
        />
      </div>
    );
  }
  if (slug === "simple-interest" || slug === "compound-interest") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Principal"
          value={fmtINR(result.principal)}
          testid="result-principal"
        />
        <ResultStat
          label="Interest"
          value={fmtINR(result.interest)}
          accent="text-emerald-600"
          testid="result-interest"
        />
        <ResultStat
          label="Total"
          value={fmtINR(result.total_amount)}
          testid="result-total"
        />
      </div>
    );
  }
  if (slug === "goal-planning") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Monthly SIP Required"
          value={fmtINR(result.monthly_sip_required)}
          accent="text-emerald-600"
          testid="result-monthly-sip"
        />
        <ResultStat
          label="From Current Savings"
          value={fmtINR(result.fv_current_savings)}
          testid="result-fv-current"
        />
        <ResultStat
          label="Additional Needed"
          value={fmtINR(result.additional_needed)}
          testid="result-additional-needed"
        />
      </div>
    );
  }
  if (slug === "net-worth") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Total Assets"
          value={fmtINR(result.total_assets)}
          testid="result-assets"
        />
        <ResultStat
          label="Total Liabilities"
          value={fmtINR(result.total_liabilities)}
          testid="result-liabilities"
        />
        <ResultStat
          label="Net Worth"
          value={fmtINR(result.net_worth)}
          accent="text-emerald-600"
          testid="result-net-worth"
        />
      </div>
    );
  }
  if (slug === "real-estate-roi") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Total Return"
          value={fmtINR(result.total_return)}
          accent="text-emerald-600"
          testid="result-total-return"
        />
        <ResultStat
          label="ROI"
          value={`${result.roi_percent}%`}
          testid="result-roi"
        />
        <ResultStat
          label="Capital CAGR"
          value={`${result.capital_cagr_percent}%`}
          testid="result-cagr"
        />
      </div>
    );
  }
  if (slug === "loan-eligibility") {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        <ResultStat
          label="Eligible Loan"
          value={fmtINR(result.eligible_loan)}
          accent="text-emerald-600"
          testid="result-eligible-loan"
        />
        <ResultStat
          label="Max EMI Affordable"
          value={fmtINR(result.max_emi)}
          testid="result-max-emi"
        />
      </div>
    );
  }
  if (slug === "debt-payoff") {
    if (result.months_to_payoff === -1) {
      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 p-5 text-amber-800 dark:text-amber-300 text-sm">
          {result.note}
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Months to Payoff"
          value={result.months_to_payoff}
          accent="text-emerald-600"
          testid="result-months"
        />
        <ResultStat
          label="Total Interest"
          value={fmtINR(result.total_interest)}
          testid="result-total-interest"
        />
        <ResultStat
          label="Total Paid"
          value={fmtINR(result.total_paid)}
          testid="result-total-paid"
        />
      </div>
    );
  }
  if (slug === "asset-allocation") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        <ResultStat
          label="Equity"
          value={`${result.equity_percent}%`}
          accent="text-emerald-600"
          testid="result-equity"
        />
        <ResultStat
          label="Debt"
          value={`${result.debt_percent}%`}
          testid="result-debt"
        />
        <ResultStat
          label="Gold"
          value={`${result.gold_percent}%`}
          testid="result-gold"
        />
      </div>
    );
  }
  // Default investment-like
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <ResultStat
        label={
          slug === "rd" || slug === "fd" || slug === "ppf"
            ? "Maturity Value"
            : "Future Value"
        }
        value={fmtINR(result.future_value || result.maturity_value)}
        accent="text-emerald-600"
        testid="result-future-value"
      />
      <ResultStat
        label={
          slug === "rd" || slug === "fd" || slug === "ppf"
            ? "Invested"
            : "Total Invested"
        }
        value={fmtINR(result.invested || result.principal)}
        testid="result-invested"
      />
      <ResultStat
        label={
          slug === "rd" || slug === "fd" || slug === "ppf"
            ? "Interest Earned"
            : "Total Gains"
        }
        value={fmtINR(result.gains || result.interest_earned)}
        testid="result-gains"
      />
    </div>
  );
}

function ChartBlock({ slug, result, cfg }) {
  const type = cfg.chartType;
  if (type === "none") return null;

  if (type === "pie") {
    const data = result.allocation || [];
    return (
      <div className="card-soft p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
          Allocation
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (type === "emi") {
    const data = result.schedule || [];
    return (
      <div className="card-soft p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
          Yearly Amortisation
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={data}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="rgba(148,163,184,0.2)"
              />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 11 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <YAxis
                tickFormatter={(v) => fmtINR(v, { compact: true })}
                tick={{ fontSize: 11 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Legend />
              <Bar
                dataKey="principal"
                stackId="a"
                fill="#10b981"
                name="Principal"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="interest"
                stackId="a"
                fill="#1e3a8a"
                name="Interest"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (type === "balance") {
    const data = result.series || [];
    return (
      <div className="card-soft p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
          Corpus Trajectory
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="g-bal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="rgba(148,163,184,0.2)"
              />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 11 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <YAxis
                tickFormatter={(v) => fmtINR(v, { compact: true })}
                tick={{ fontSize: 11 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#g-bal)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // line type default
  const data = result.series || [];
  return (
    <div className="card-soft p-6">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
        Growth Trajectory
      </div>
      <div className="h-72">
        <ResponsiveContainer>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="g-val" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="g-inv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e3a8a" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#1e3a8a" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="rgba(148,163,184,0.2)"
            />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
            />
            <YAxis
              tickFormatter={(v) => fmtINR(v, { compact: true })}
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
            />
            <Tooltip formatter={(v) => fmtINR(v)} />
            {data[0]?.invested !== undefined && (
              <Area
                type="monotone"
                dataKey="invested"
                stroke="#1e3a8a"
                strokeWidth={1.5}
                fill="url(#g-inv)"
                name="Invested"
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke="#10b981"
              strokeWidth={2.5}
              fill="url(#g-val)"
              name="Value"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
